import React, { useEffect, useState } from 'react';
import { COLORS, Row } from '@cuemath/comps';
import { Button, Separator, Text } from '@cuemath/web-comps';
import styled from 'styled-components';
import moment from 'moment';

import { patch as editStudentDemo } from 'actions/student-demos';
import { POCValue, POCWrapper } from 'screens/student-details/styled-comps';
import { CopyButton } from 'comps';
import Like from 'img/like.svg';
import { useParams } from 'react-router-dom';
import Dislike from 'img/dislike.svg';
import ClosableBox from 'comps/closable-box';
import CueSelect from 'comps/inputs/cueSelect';
import CueTextArea from 'comps/inputs/cueTextArea';
import CueCalenderInput from 'comps/inputs/cueCalendarInput';
import { patch as patchCourseClassLog } from 'actions/course-class-log';
import { patch as patchTeacherClassroom } from 'actions/teacher-classrooms';
import { trackEvent } from 'modules/analytics';
import { EVENT_TYPE } from 'constants/logger';
import CueTimeInput from 'comps/inputs/cueTimeInput';
import CueTextInput from 'comps/inputs/cueTextInput';
import { formatTimestamp, convertEpochToDateFormat } from 'utils';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { Message } from 'modules/form';
import { useStudentGet } from 'actions/studentsv2';
import { store } from 'redux-rest-reducer';
import { DEFAULT_CLASS_DURATION } from 'constants/form';
import { invalidateSessions } from 'actions/studentsv2';
import { useCallback } from 'react';

const Root = styled.div`
  min-height: 556px;
`;

const PaymentInfoHeading = styled(Text)`
  min-width: ${({ minWidth }) => (minWidth ? minWidth : '152px')};
  margin-right: 16px;
  color: rgba(43, 43, 43, 0.75);
  line-height: 18px;
  cursor: ${({ cursor }) => (cursor ? cursor : 'default')};
  width: ${({ width }) => (width ? width : 'auto')};
`;

const CustomRow = styled(Row)`
  margin-top: ${({ marginTop }) => (marginTop ? marginTop : '20px')};
`;

const PTMFeedbackWrapper = styled.div`
  border: 1px solid ${COLORS.GHOST};
  border-radius: 8px;
  margin-top: 16px;
  padding: 16px 12px;
  background: ${COLORS.BG_CLOUD};
`;

const FeedbackQuestion = styled(Text)`
  max-width: 236px;
  margin-top: 12px;
`;

const FormWrapper = styled.div`
  flex-direction: column;
  align-items: flex-end;
  display: flex;
`;

const CancelDemoButton = styled(Button)`
  margin-right: 16px;
`;

const POCValueWrapper = styled.div`
  max-width: ${({ width }) => (width ? width : 122)}px;
  overflow-wrap: break-word;
`;

function SessionInfo(props) {
  const { id: studentId } = useParams();
  const [clasStatusChange, setClassStatusChange] = useState('no');
  const [error, setError] = useState(''); // class Invalidation errors
  const [success, setSuccess] = useState('');
  const [reasonForInvalid, setReasonForInvalid] = useState('');
  const [updateDemo, setUpdateDemo] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const currentDate = moment().format('YYYY-MM-DD');
  const [updateDemoDuration, setUpdateDemoDuration] = useState('');
  const [errorText, setErrorText] = useState('');
  const teacherClassrooms = useSelector(state => state.teacherClassrooms);
  const [classroomPatch] = useState(store.patch());
  const { id: classroomPatchId } = classroomPatch || {};
  const patchClassroom = store.patch(teacherClassrooms, classroomPatchId);
  const isClassroomPatched = patchClassroom?.resolved?.isPatched ?? false;
  const isClassroomPatchingFailed = patchClassroom?.resolved?.isPatchingFailed ?? false;
  const isClassroomPatching = patchClassroom?.current?.isPatching ?? false;
  
  const {
    id,
    actual_class_duration_minutes: classDuration,
    enter_datetime: enterTime,
    exit_datetime: exitTime,
    tag: classType,
    classroom_summary: classSummeryLink,
    teacher_details: { name: teacherName },
    ptm_details: ptmDetails,
    class_log: classLog,
    actions,
    demo_details: demoDetails,
    studentDemos,
    courseClassLog,
    class_status: classStatus,
    student_slot_id: studentSlotId,
    teacher_classroom_id: teacherClassroomId,
  } = props || {};
  const {
    demo_state: demoState,
    source,
    is_direct_demo_allotted: isDirectDemoAllotted,
    demo_request_id: demoRequestId,
    created_on: createdOn,
    created_by_details: createdByDetails,
    id: demoDetailsId,
    program_id: programId,
    state,
    recent_classroom_id: recentClassroomId,
  } = demoDetails || {};
  const { first_name: createdByFirstName, last_name: createdByLastName } =
    createdByDetails || {};
  const newStudentPatch = store.patch(studentDemos, studentId);
  const { resolved } = newStudentPatch || {};
  const { data: studentDemoData, isPatched, isPatchingFailed } = resolved || {};
  const { created_on: updatedCreatedOn } = studentDemoData || {};
  const { data: studentDetails } = useStudentGet(studentId) || {};
  const { is_plus_lead: isPlusLead } = studentDetails || {};

  const { is_invalid: isInvalid, id: logId } = classLog || {};

  const classInvalidPatch = store.patch(courseClassLog, id);
  const { current: classInvalidCurrent, resolved: classInvalidResolved } =
    classInvalidPatch || {};
  const { isPatched: classInvalidPatched, isPatchingFailed: classinvalidPatchFailed } =
    classInvalidResolved || {};
  const { isPatching: classInvalidPatching } = classInvalidCurrent || {};
  const isSessionUpdatable = classStatus === 'IN FUTURE' || classStatus === 'ONGOING';

  const { feedback, id: ptmId } = ptmDetails || {};
  const { parent_feedback: parentFeedback } = feedback || {};
  const formattedEnterTime = enterTime && formatTimestamp(enterTime);
  const formattedExitTime = exitTime && formatTimestamp(exitTime);
  let formattedCreatedOnDate = createdOn && convertEpochToDateFormat(createdOn * 1000);
  let formattedCreatedOnTime = createdOn && formatTimestamp(createdOn);

  if (updatedCreatedOn) {
    formattedCreatedOnDate = convertEpochToDateFormat(updatedCreatedOn * 1000);
    formattedCreatedOnTime = formatTimestamp(updatedCreatedOn);
  }

  const openLink = (link, event) => {
    trackEvent(event, {
      session_id: id,
    });
    window.open(link);
  };

  useEffect(() => {
    if (classinvalidPatchFailed) {
      const errorMsg = store.getError(classInvalidResolved);
      setError(errorMsg ? errorMsg.message : 'Class Invalidation Failed');
      setSuccess('');
    }
    if (classInvalidPatched) {
      setError('');
      setSuccess('Class Invalidated Successfully');
    }
  }, [classInvalidPatched, classInvalidResolved, classinvalidPatchFailed, error]);

  useEffect(() => {
    if (resolved && isPatched) {
      setErrorText('');
      setUpdateDemo(false);
      invalidateSessions(studentId);
    } else if (resolved && isPatchingFailed) {
      setErrorText(store.getError(resolved) ? store.getError(resolved).message : 'Something went wrong');
    }
  }, [studentId, resolved, isPatchingFailed, isPatched]);
  
  useEffect(() => {
    if (patchClassroom?.resolved && isClassroomPatched) {
      setErrorText('');
      invalidateSessions(studentId);
    } else if (patchClassroom?.resolved && isClassroomPatchingFailed) {
      setErrorText(store.getError(patchClassroom?.resolved)?.message || 'Something went wrong');
    }
  }, [studentId, patchClassroom?.resolved, isClassroomPatchingFailed, isClassroomPatched]);
  
  const handleCancelTrainingClass = useCallback(() => {
    actions.patchTeacherClassroom(classroomPatchId, teacherClassroomId, {
      action: 'exit',
    });

    trackEvent(EVENT_TYPE.CANCEL_TRAINING_CLASS_CLICK, {
      session_id: id,
    });
  }, [actions, classroomPatchId, teacherClassroomId, id]);

  const handleCancelDemo = useCallback(() => {
    actions.editStudentDemo(studentId, demoDetailsId, {
      action: 'SET_SCHEDULE',
      action_data: {
        scheduled_start_time: null,
        scheduled_end_time: null,
      },
    });

    trackEvent(EVENT_TYPE.CANCEL_DEMO_CLICK, {
      session_id: id,
    });
  }, [actions, demoDetailsId, id, studentId]);

  const getExtraLabels = () => {
    switch (classType.toLowerCase()) {
      case 'demo':
        return (
          <CustomRow>
            <PaymentInfoHeading variant="eyebrow2">Demo Feedback</PaymentInfoHeading>
            {state !== 'INTERESTED' ? (
              <POCValue
                textDecoration="underline"
                variant="cta1"
                onClick={() =>
                  recentClassroomId &&
                  openLink(`/demo/${recentClassroomId}`, EVENT_TYPE.DEMO_FEEDBACK_CLICK)
                }
              >
                {recentClassroomId ? 'View' : '-'}
              </POCValue>
            ) : (
              '-'
            )}
          </CustomRow>
        );
      case 'ptm':
        return (
          <CustomRow>
            <PaymentInfoHeading variant="eyebrow2">Report</PaymentInfoHeading>
            <POCValue
              textDecoration="underline"
              variant="cta1"
              onClick={() =>
                ptmId && openLink(`/ptm-report/${ptmId}`, EVENT_TYPE.PTM_REPORT_CLICK)
              }
            >
              {ptmId ? 'View' : '-'}
            </POCValue>
          </CustomRow>
        );
      case 'regular':
      case 'extra':
      default:
        return null;
    }
  };

  const handleUpdateDemoClass = useCallback(() => {
    const duration = updateDemoDuration || DEFAULT_CLASS_DURATION;
    const startDateObj = `${startDate} ${startTime}`;
    const newDate = moment(startDateObj).format('X');
    const endTime = moment(startDateObj)
      .add(parseInt(duration, 10), 'minutes')
      .format('X');

    actions.editStudentDemo(studentId, demoDetailsId, {
      action: 'SET_SCHEDULE',
      action_data: {
        scheduled_start_time: newDate,
        scheduled_end_time: endTime,
        is_plus_lead: isPlusLead,
      },
    });

    trackEvent(EVENT_TYPE.UPDATE_DEMO_CLICK, {
      session_id: id,
    });
  }, [actions, demoDetailsId, id, isPlusLead, studentId, startDate, startTime, updateDemoDuration]);

  const invalidClassLog = () => {
    actions.patchCourseClassLog(id, logId, {
      action: 'mark_invalid',
      action_data: { reason: reasonForInvalid },
    });
  };

  const getExtraFields = () => {
    switch (classType.toLowerCase()) {
      case 'demo':
        return (
          <>
            {demoDetails && demoDetailsId && (
              <>
                <CustomRow>
                  <PaymentInfoHeading variant="eyebrow2">Program ID</PaymentInfoHeading>
                </CustomRow>
                <CustomRow marginTop="4px">
                  <CopyButton copyMessage="Copy Program Id" text={programId || '-'} />
                </CustomRow>
                <CustomRow>
                  <PaymentInfoHeading variant="eyebrow2">State</PaymentInfoHeading>
                  <POCValueWrapper>
                    <POCValue variant="cta1">{demoState || '-'}</POCValue>
                  </POCValueWrapper>
                </CustomRow>
                <CustomRow>
                  <PaymentInfoHeading variant="eyebrow2">SOURCE</PaymentInfoHeading>
                  <POCValueWrapper>
                    <POCValue variant="cta1">{source || '-'}</POCValue>
                  </POCValueWrapper>
                </CustomRow>
                <CustomRow>
                  <PaymentInfoHeading width="152px" variant="eyebrow2">
                    Auto Demo Allocation
                  </PaymentInfoHeading>
                  <POCValueWrapper>
                    <POCValue variant="cta1">{`${!!isDirectDemoAllotted}`}</POCValue>
                  </POCValueWrapper>
                </CustomRow>
                <CustomRow>
                  <PaymentInfoHeading width="152px" variant="eyebrow2">
                    Direct Demo Allocation
                  </PaymentInfoHeading>
                  <POCValueWrapper>
                    <POCValue variant="cta1">{`${!!demoRequestId}`}</POCValue>
                  </POCValueWrapper>
                </CustomRow>
                <CustomRow>
                  <PaymentInfoHeading variant="eyebrow2">Created On</PaymentInfoHeading>
                  <POCValueWrapper>
                    <POCValue variant="cta1">
                      {formattedCreatedOnDate || '-'}, {formattedCreatedOnTime || '-'}
                    </POCValue>
                  </POCValueWrapper>
                </CustomRow>
                <CustomRow>
                  <PaymentInfoHeading variant="eyebrow2">Created By</PaymentInfoHeading>
                  <POCValueWrapper>
                    <POCValue variant="cta1">
                      {createdByFirstName
                        ? `${createdByFirstName} ${createdByLastName}`
                        : '-'}
                    </POCValue>
                  </POCValueWrapper>
                </CustomRow>
                <CustomRow>
                  <Button
                    width="100%"
                    size="small"
                    onClick={() => setUpdateDemo(true)}
                    isDisabled={updateDemo || !demoDetailsId}
                  >
                    Update Demo
                  </Button>
                </CustomRow>
                {updateDemo ? (
                  <ClosableBox title="UPDATE DEMO" onClose={() => setUpdateDemo(false)}>
                    {errorText && <Message error={true}>{errorText}</Message>}
                    <CustomRow>
                      <PaymentInfoHeading variant="eyebrow2">Teacher</PaymentInfoHeading>
                      <POCValueWrapper width={116}>
                        <POCValue variant="cta1">{teacherName || '-'}</POCValue>
                      </POCValueWrapper>
                    </CustomRow>
                    <Separator height={16} transparent />
                    <CueCalenderInput
                      label="demo Date"
                      width="280"
                      required={true}
                      value={startDate}
                      min={currentDate}
                      onChange={setStartDate}
                    />
                    <Separator height={24} transparent />
                    <CueTimeInput
                      label="demo time"
                      name="startTime"
                      type="time"
                      width="280"
                      required={true}
                      value={startTime}
                      onChange={setStartTime}
                    />
                    <Separator height={16} transparent />
                    <CueTextInput
                      label="Duration"
                      value={updateDemoDuration}
                      onChange={setUpdateDemoDuration}
                    />
                    <Separator height={16} transparent />
                    <Row justifyContent="flex-end">
                      <CancelDemoButton
                        width="120px"
                        variant="secondary"
                        size="xsmall"
                        onClick={handleCancelDemo}
                      >
                        Cancel Demo
                      </CancelDemoButton>
                      <Button onClick={handleUpdateDemoClass} size="xsmall" width="120px">
                        Update
                      </Button>
                    </Row>
                  </ClosableBox>
                ) : null}
              </>
            )}
          </>
        );
      case 'teacher_training':
        return (
          <>
            {isSessionUpdatable && (
              <>
                {errorText && <Message error={true}>{errorText}</Message>}
                <CustomRow>
                  <Button
                    width="100%"
                    size="small"
                    variant="secondary"
                    onClick={handleCancelTrainingClass}
                    isBusy={isClassroomPatching}
                    isDisabled={isClassroomPatched}
                  >
                    Cancel Training Class
                  </Button>
                </CustomRow>
              </>
            )}
          </>
        );
      case 'ptm':
        return (
          <>
            <CustomRow>
              <PaymentInfoHeading variant="eyebrow2">PTM ID</PaymentInfoHeading>
            </CustomRow>
            <CustomRow marginTop="4px">
              <CopyButton copyMessage="Copy PTM Id" text={ptmId || '-'} />
            </CustomRow>
            {parentFeedback?.length && (
              <PTMFeedbackWrapper>
                <PaymentInfoHeading variant="eyebrow2">Feedback</PaymentInfoHeading>
                {parentFeedback?.map(feedback => {
                  const { question, liked } = feedback;
                  return (
                    <Row justifyContent="space-between">
                      <FeedbackQuestion variant="body2">{question}</FeedbackQuestion>
                      <img src={liked ? Like : Dislike} alt="" />
                    </Row>
                  );
                })}
              </PTMFeedbackWrapper>
            )}
          </>
        );
      case 'regular':
      case 'extra':
      default:
        return (
          <div>
            <Separator height={24} transparent />
            {!isInvalid && isInvalid !== undefined && (
              <CueSelect
                label="Do you want to mark invalid?*"
                options={[
                  { text: 'Yes', value: 'yes' },
                  { text: 'No', value: 'no' },
                ]}
                onClick={setClassStatusChange}
                defaultValue="no"
                fluid
              />
            )}
            {clasStatusChange === 'yes' ? (
              <FormWrapper>
                <Separator height={16} transparent />
                <CueTextArea
                  placeholder={'Reason for marking invalid'}
                  onChange={setReasonForInvalid}
                  fluid
                  rows={2}
                />

                {error && (
                  <Message width={'100%'} error={true}>
                    {error}
                  </Message>
                )}
                {success && <Message width={'100%'}>{success}</Message>}
                <Separator height={16} transparent />
                <Button
                  isDisabled={!reasonForInvalid || success}
                  onClick={invalidClassLog}
                  size="small"
                  isBusy={classInvalidPatching}
                >
                  Submit
                </Button>
              </FormWrapper>
            ) : null}
          </div>
        );
    }
  };

  return (
    <Root>
      <POCWrapper>
        <CustomRow>
          <PaymentInfoHeading variant="eyebrow2">ENTRY TIME</PaymentInfoHeading>
          <POCValue variant="cta1">{formattedEnterTime || '-'}</POCValue>
        </CustomRow>
        <CustomRow>
          <PaymentInfoHeading variant="eyebrow2">EXIT TIME</PaymentInfoHeading>
          <POCValue variant="cta1">{formattedExitTime || '-'}</POCValue>
        </CustomRow>
        <CustomRow>
          <PaymentInfoHeading variant="eyebrow2">DURATION</PaymentInfoHeading>
          <POCValue variant="cta1">
            {(classDuration && Number.parseFloat(classDuration).toFixed(2)) || '-'}
          </POCValue>
        </CustomRow>
        <CustomRow>
          <PaymentInfoHeading variant="eyebrow2">CLASS SUMMARY</PaymentInfoHeading>
          <POCValue
            onClick={() =>
              classSummeryLink &&
              openLink(classSummeryLink, EVENT_TYPE.CLASS_SUMMARY_CLICK)
            }
            textDecoration="underline"
            variant="cta1"
          >
            {classSummeryLink ? 'View' : '-'}
          </POCValue>
        </CustomRow>
        {getExtraLabels()}
        <CustomRow>
          <PaymentInfoHeading variant="eyebrow2">Classroom ID</PaymentInfoHeading>
        </CustomRow>
        <CustomRow marginTop="4px">
          <CopyButton copyMessage="Copy Classroom Id" text={id || '-'} />
        </CustomRow>
        {getExtraFields()}
      </POCWrapper>
    </Root>
  );
}

export default connect(
  ({ courseClassLog, studentDemos }) => ({
    courseClassLog,
    studentDemos,
  }),
  dispatch => ({
    actions: bindActionCreators(
      {
        patchCourseClassLog,
        editStudentDemo,
        patchTeacherClassroom,
      },
      dispatch,
    ),
  }),
)(SessionInfo);
